import { useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Map server doc -> client-facing copy object
const norm = (c) => ({
  id: c.id,
  traderId: c.trader_id,
  traderName: c.trader_name,
  traderAvatar: c.trader_avatar,
  traderColor: c.trader_color,
  traderBadge: c.trader_badge,
  traderMonthlyReturn: c.trader_monthly_return || 0,
  allocated: Number(c.allocated || 0),
  multiplier: Number(c.multiplier || 1),
  currentValue: Number(c.current_value || 0),
  status: c.status || "active",
  copiedAt: c.created_at,
});

export const useCopies = () => {
  const { user } = useAuth();
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(false);
  const driftTimer = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setCopies([]);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get("/copies");
      const items = (data || []).map(norm);
      setCopies(items);
      return items;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Local visual drift between server refreshes (purely cosmetic — server is the source of truth)
  useEffect(() => {
    if (!copies.length) return;
    if (driftTimer.current) clearInterval(driftTimer.current);
    driftTimer.current = setInterval(() => {
      setCopies((prev) =>
        prev.map((c) => {
          if (c.status !== "active") return c;
          const dailyReturn = c.traderMonthlyReturn / 30 / 100;
          const tickReturn = dailyReturn / 28800;
          const noise = (Math.random() - 0.48) * Math.abs(tickReturn) * 60;
          const change = c.allocated * (tickReturn + noise) * c.multiplier;
          const newValue = Math.max(c.allocated * 0.5, c.currentValue + change);
          return { ...c, currentValue: Math.round(newValue * 100) / 100 };
        })
      );
    }, 3000);
    return () => {
      if (driftTimer.current) clearInterval(driftTimer.current);
    };
  }, [copies.length]);

  // Server reconcile every 25s
  useEffect(() => {
    const t = setInterval(() => {
      if (user) refresh();
    }, 25000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const addCopy = useCallback(
    async (trader, allocated, multiplier) => {
      const { data } = await api.post("/copies", {
        trader_id: trader.id,
        trader_name: trader.name,
        trader_avatar: trader.avatar,
        trader_color: trader.color,
        trader_badge: trader.badge,
        trader_monthly_return: trader.monthlyReturn,
        allocated: Number(allocated),
        multiplier: Number(multiplier),
      });
      const item = norm(data);
      setCopies((p) => [...p, item]);
      return item;
    },
    []
  );

  const pauseCopy = useCallback(async (id) => {
    const cur = copies.find((c) => c.id === id);
    if (!cur) return;
    const next = cur.status === "active" ? "paused" : "active";
    setCopies((p) => p.map((c) => (c.id === id ? { ...c, status: next } : c)));
    try {
      await api.put(`/copies/${id}/status`, { status: next });
    } catch {
      await refresh();
    }
  }, [copies, refresh]);

  const stopCopy = useCallback(async (id) => {
    setCopies((p) => p.filter((c) => c.id !== id));
    try {
      await api.delete(`/copies/${id}`);
    } catch {
      await refresh();
    }
  }, [refresh]);

  const isCopying = useCallback(
    (traderId) => copies.some((c) => c.traderId === traderId && c.status !== "stopped"),
    [copies]
  );

  const totalAllocated = copies.reduce((s, c) => s + c.allocated, 0);
  const totalValue = copies.reduce((s, c) => s + c.currentValue, 0);
  const totalPnL = totalValue - totalAllocated;
  const totalReturnPct = totalAllocated > 0 ? (totalPnL / totalAllocated) * 100 : 0;

  return {
    copies,
    loading,
    addCopy,
    pauseCopy,
    stopCopy,
    isCopying,
    refresh,
    totalAllocated,
    totalValue,
    totalPnL,
    totalReturnPct,
  };
};
